// Runs (RF-UI-13, RF-OBS-04): attempts from GET /v1/attempts — across every project the
// caller can read, or one project — newest first, cursor-paginated and filterable by state.
// Each row shows task, attempt, role, state, measured time and the attempt it re-ran;
// expanding opens the task's work tree.
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useI18n } from "../../i18n/index.tsx";
import { listAttempts } from "../../api/endpoints-wave2.ts";
import { ATTEMPT_STATES, type AttemptListItem } from "../../api/types-wave2.ts";
import { STEP_VIEW } from "../../lib/status.ts";
import { elapsedMs, formatMs, useNow } from "../../lib/format.ts";
import { useDebouncedCallback } from "../../lib/useAsync.ts";
import { useRealtimeEvents } from "../../realtime/RealtimeProvider.tsx";
import { StatusIcon } from "../../components/Status.tsx";
import { Button } from "../../components/Button.tsx";
import { SelectField } from "../../components/Field.tsx";
import { EmptyState, ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { PlayCircleIcon, RefreshIcon } from "../../components/icons.tsx";
import { TraceView, runStateOf } from "./TraceView.tsx";

const PAGE = 50;

export function RunsList({ projectId, projectName }: { projectId: string | null; projectName?: (id: string) => string | null }) {
  const { t, formatDateTime } = useI18n();
  const now = useNow(1000);
  const [state, setState] = useState("");
  const [items, setItems] = useState<AttemptListItem[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [moreError, setMoreError] = useState<unknown>(null);
  const [moreBusy, setMoreBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    listAttempts({ project_id: projectId, state: state || null, limit: PAGE }, controller.signal)
      .then((page) => {
        setItems(page.items);
        setCursor(page.next_cursor);
      })
      .catch((e: unknown) => {
        if (!controller.signal.aborted && !(e instanceof DOMException && e.name === "AbortError")) setError(e);
      });
    return () => controller.abort();
  }, [projectId, state, refreshKey]);

  const refresh = useDebouncedCallback(() => setRefreshKey((n) => n + 1), 1500);
  useRealtimeEvents((e) => {
    if (e.type === "run.started" || e.type === "task.transitioned" || e.type === "task.completed") refresh();
  }, projectId);

  const loadMore = async () => {
    if (!cursor) return;
    setMoreBusy(true);
    setMoreError(null);
    try {
      const page = await listAttempts({ project_id: projectId, state: state || null, cursor, limit: PAGE });
      setItems((list) => [...(list ?? []), ...page.items]);
      setCursor(page.next_cursor);
    } catch (e) {
      setMoreError(e);
    } finally {
      setMoreBusy(false);
    }
  };

  const numberOf = new Map((items ?? []).map((a) => [a.id, a.number]));
  const openItem = items?.find((a) => a.id === open) ?? null;

  return (
    <section className="card runs-list" aria-labelledby={`runs-${projectId ?? "all"}-title`} data-testid="runs-list">
      <div className="card-header">
        <h2 id={`runs-${projectId ?? "all"}-title`} className="card-title" style={{ flex: 1 }}>
          {t("runs.attempts")}
        </h2>
        <Button size="sm" variant="ghost" icon={<RefreshIcon size={14} />} onClick={() => setRefreshKey((n) => n + 1)}>
          {t("common.refresh")}
        </Button>
      </div>
      <div className="card-body stack">
        <div className="runs-toolbar">
          <SelectField label={t("runs.stateFilter")} value={state} onChange={(e) => setState(e.target.value)}>
            <option value="">{t("common.all")}</option>
            {ATTEMPT_STATES.map((s) => (
              <option key={s} value={s}>
                {t(`attemptState.${s}`)}
              </option>
            ))}
          </SelectField>
        </div>
        {error && !items ? (
          <ErrorBanner error={error} onRetry={() => setRefreshKey((n) => n + 1)} />
        ) : items === null ? (
          <LoadingBlock label={t("common.loading")} />
        ) : items.length === 0 ? (
          <EmptyState icon={<PlayCircleIcon />} title={t("runs.emptyTitle")} headingLevel={3}>
            <p>{t("runs.emptyBody")}</p>
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <caption className="sr-only">{t("runs.attempts")}</caption>
              <thead>
                <tr>
                  {projectId ? null : <th scope="col">{t("runs.project")}</th>}
                  <th scope="col">{t("runs.task")}</th>
                  <th scope="col">{t("runs.attempt")}</th>
                  <th scope="col">{t("agents.role")}</th>
                  <th scope="col">{t("runs.state")}</th>
                  <th scope="col">{t("runs.started")}</th>
                  <th scope="col">{t("runs.duration")}</th>
                  <th scope="col">
                    <span className="sr-only">{t("common.actions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => {
                  const st = runStateOf(a.state);
                  const view = STEP_VIEW[st];
                  const ms = elapsedMs(a.started_at, a.ended_at, now);
                  const previous = a.supersedes_attempt_id ? numberOf.get(a.supersedes_attempt_id) : undefined;
                  return (
                    <tr key={a.id} data-testid="run-row" data-state={st}>
                      {projectId ? null : (
                        <td>
                          <Link to={`/projects/${a.project_id}`} className="link-subtle">
                            {projectName?.(a.project_id) ?? a.project_id.slice(0, 8)}
                          </Link>
                        </td>
                      )}
                      <td>
                        <Link to={`/projects/${a.project_id}/kanban?task=${a.task_id}`} className="link-subtle">
                          <span className="mono">#{a.task_number}</span> <span className="user-content">{a.task_title}</span>
                        </Link>
                      </td>
                      <td>
                        #{a.number}
                        {a.supersedes_attempt_id ? (
                          <span className="subtle" style={{ display: "block" }}>
                            {previous !== undefined ? t("trace.rerunOf", { n: previous }) : t("runs.rerun")}
                          </span>
                        ) : null}
                      </td>
                      <td>{a.role}</td>
                      <td>
                        <span className={`row tone-${view.tone}`}>
                          <StatusIcon kind={view.icon} size={14} />
                          {t(view.label)}
                        </span>
                        {a.end_reason ? (
                          <span className="subtle user-content" style={{ display: "block" }}>
                            {a.end_reason}
                          </span>
                        ) : null}
                      </td>
                      <td>{formatDateTime(a.started_at)}</td>
                      <td className="mono">{ms === null ? "—" : a.ended_at ? formatMs(ms) : t("trace.elapsed", { value: formatMs(ms) })}</td>
                      <td>
                        <Button size="sm" variant="ghost" aria-expanded={open === a.id} onClick={() => setOpen((o) => (o === a.id ? null : a.id))}>
                          {open === a.id ? t("runs.hideTree") : t("runs.showTree")}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {moreError ? <ErrorBanner error={moreError} onRetry={() => void loadMore()} /> : null}
        {cursor ? (
          <div>
            <Button size="sm" onClick={() => void loadMore()} loading={moreBusy}>
              {t("obs.loadMore")}
            </Button>
          </div>
        ) : items && items.length > 0 ? (
          <p className="field-hint">{t("runs.endOfList", { n: items.length })}</p>
        ) : null}
        {openItem ? (
          <div className="card card--pad" data-testid="runs-tree">
            <p className="subheading">
              <span className="mono">#{openItem.task_number}</span> {openItem.task_title}
            </p>
            <TraceView taskId={openItem.task_id} projectId={openItem.project_id} />
          </div>
        ) : null}
      </div>
    </section>
  );
}
